/**
 * CustomCTASection - Renders a single admin-created custom CTA row.
 *
 * Reads one entry from `content.custom_ctas` (an array). Same styling as
 * PrimaryCTASection so custom CTAs feel native, but each carries its own
 * copy, action, and optional background image. Section ID is dynamic
 * (`custom_cta_<random>`) so the Layout tab and analytics can address it.
 */
import { Button } from '../ui/button';
import { ChevronRight } from 'lucide-react';
import { trackButtonClick } from '../../utils/analytics';
import { resolveCtaClick } from '../../utils/ctaActions';
import { BRAND_GREEN, BRAND_GREEN_LIGHT, BRAND_GREEN_DARK } from './landingConstants';
import { ctaBackgroundStyle } from './ctaBackgroundStyle';

export function CustomCTASection({ cta, goToSignup }) {
  if (!cta || !cta.id) return null;

  const bgStyle = ctaBackgroundStyle(cta);

  return (
    <section
      key={cta.id}
      id={cta.id}
      data-section={cta.id}
      data-testid={`custom-cta-section-${cta.id}`}
      className="py-20 px-4 sm:px-6 lg:px-8"
    >
      <div
        className="max-w-4xl mx-auto rounded-3xl p-12 text-center relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${BRAND_GREEN_DARK} 0%, ${BRAND_GREEN} 50%, ${BRAND_GREEN_LIGHT} 100%)` }}
      >
        {bgStyle && (
          <div
            aria-hidden="true"
            data-testid={`custom-cta-bg-image-${cta.id}`}
            className="pointer-events-none absolute inset-0 rounded-3xl"
            style={bgStyle}
          />
        )}
        <div className="relative z-10">
          {cta.title && (
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{cta.title}</h2>
          )}
          {cta.subtitle && (
            <p className="text-white/80 text-lg mb-8">{cta.subtitle}</p>
          )}
          {cta.buttonText && (
            <Button
              size="lg"
              className="bg-white text-[#007a33] hover:bg-gray-100 text-lg px-8 py-6 rounded-xl"
              onClick={() => { trackButtonClick(`custom_cta_${cta.id}`, cta.buttonText); resolveCtaClick(cta, goToSignup); }}
              data-testid={`custom-cta-btn-${cta.id}`}
            >
              {cta.buttonText}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          )}
          {cta.secondaryText && (
            <p className="text-white/80 text-sm mt-4">{cta.secondaryText}</p>
          )}
        </div>
      </div>
    </section>
  );
}

export default CustomCTASection;
